import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import { Select, Checkbox } from '../common/Form';
import Button from '../common/Button';

export function countActiveSessionFilters(filters) {
  return Object.keys(filters).filter((key) => filters[key] != null && filters[key] !== '').length;
}

const SessionFilters = ({
  filters,
  onFiltersChange,
  expanded = false,
  showCancelledSessions = false,
  onShowCancelledChange,
}) => {
  const [sessionTypes, setSessionTypes] = useState([]);

  useEffect(() => {
    fetchSessionTypes();
  }, []);

  const fetchSessionTypes = async () => {
    try {
      const { data, error } = await supabase
        .from('session_types')
        .select('*')
        .order('name');

      if (error) throw error;
      setSessionTypes(data);
    } catch (err) {
      console.error('Error fetching session types:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters };

    if (value === '' || value === null) {
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }

    onFiltersChange(newFilters);
  };

  const clearFilters = () => {
    onFiltersChange({});
  };

  const hasActiveFilters = countActiveSessionFilters(filters) > 0;

  const filterFields = [
    {
      key: 'session_type_id',
      label: 'Type',
      value: filters.session_type_id || '',
      placeholder: 'All types',
      options: sessionTypes.map((type) => ({ value: type.id, label: type.name })),
    },
    {
      key: 'date_range',
      label: 'Date',
      value: filters.date_range || '',
      placeholder: 'Any date',
      options: [
        { value: 'today', label: 'Today' },
        { value: 'tomorrow', label: 'Tomorrow' },
        { value: 'this_week', label: 'This week' },
        { value: 'next_week', label: 'Next week' },
        { value: 'this_month', label: 'This month' },
      ],
    },
    {
      key: 'visibility',
      label: 'Visibility',
      value: filters.visibility || '',
      placeholder: 'All sessions',
      options: [
        { value: 'public', label: 'Public only' },
        { value: 'private', label: 'Members only' },
      ],
    },
    {
      key: 'availability',
      label: 'Availability',
      value: filters.availability || '',
      placeholder: 'Any availability',
      options: [
        { value: 'open', label: 'Open spots' },
        { value: 'waitlist', label: 'Waitlist only' },
        { value: 'full', label: 'Full' },
      ],
    },
  ];

  if (!expanded) {
    return null;
  }

  return (
    <div className="space-y-4">
      {hasActiveFilters ? (
        <div className="flex justify-end">
          <Button onClick={clearFilters} variant="link" size="small" className="text-xs">
            Clear all
          </Button>
        </div>
      ) : null}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {filterFields.map((field) => (
          <div key={field.key}>
            <label className="block text-xs font-medium text-text-secondary mb-1">
              {field.label}
            </label>
            <Select
              value={field.value}
              onChange={(e) => handleFilterChange(field.key, e.target.value || null)}
              placeholder={field.placeholder}
              options={field.options}
            />
          </div>
        ))}
      </div>

      {onShowCancelledChange && (
        <Checkbox
          label="Include cancelled sessions"
          checked={showCancelledSessions}
          onChange={(e) => onShowCancelledChange(e.target.checked)}
        />
      )}
    </div>
  );
};

export function SessionFilterChips({ filters, sessionTypes = [], onFiltersChange }) {
  const handleFilterChange = (key) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    onFiltersChange(newFilters);
  };

  const chips = [];

  if (filters.session_type_id) {
    chips.push({
      key: 'session_type_id',
      label: `Type: ${sessionTypes.find((t) => t.id === filters.session_type_id)?.name || 'Selected'}`,
    });
  }
  if (filters.date_range) {
    chips.push({
      key: 'date_range',
      label: `Date: ${filters.date_range.replace(/_/g, ' ')}`,
    });
  }
  if (filters.visibility) {
    chips.push({
      key: 'visibility',
      label: filters.visibility === 'public' ? 'Public only' : 'Members only',
    });
  }
  if (filters.availability) {
    chips.push({
      key: 'availability',
      label: `Availability: ${filters.availability.replace(/_/g, ' ')}`,
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex items-center gap-1 rounded-full border border-border-light bg-surface-subtle px-2.5 py-0.5 text-xs font-medium text-text-secondary"
        >
          {chip.label}
          <button
            type="button"
            onClick={() => handleFilterChange(chip.key)}
            className="text-text-muted hover:text-text-primary leading-none"
            aria-label={`Remove ${chip.label}`}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

export default SessionFilters;
